import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { makeStyles, useTheme, withStyles } from "@mui/styles";
import {
  Toolbar,
  AppBar,
  Divider,
  Button,
  IconButton,
  Box,
  Hidden,
  Typography,
  Collapse,
  Menu as MuiMenu,
  MenuItem,
  Badge,
  useMediaQuery,
  Link as MuiLink,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuLucide, X as XLucide } from "lucide-react";
import {
  usePopupState,
  bindTrigger,
  bindMenu,
} from "material-ui-popup-state/hooks";
import Logo from "icons/new-logo.svg";
import LogoWhite from "icons/logoWhite.svg";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BusinessIcon from "@mui/icons-material/Business";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FeedIcon from "@mui/icons-material/Feed";
import { Search } from "@mui/icons-material";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import WorkOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import {
  desktopAuthenticatedSubmenus,
  mobileAuthenticatedSubmenus,
} from "bundles/common/utils/candidates/navbar";
import SelectLanguage from "./SelectLanguage";
import sh from "bundles/common/utils/sh";
import ProfilePicture from "../common/components/ProfilePicture";

export default function MainAppBar({ children }) {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const currentCandidate = useSelector((state) => state.current_candidate);
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const navigate = useNavigate();
  const theme = useTheme();
  const [messagesUnread, setMessagesUnread] = useState(0);

  useEffect(() => {
    if (!currentCandidate?.id) return;
    sh.get("/candidate/conversations/unread_count")
      .then((res) => setMessagesUnread(res.data?.unread_count || 0))
      .catch(() => {});
  }, [currentCandidate?.id]);

  const useStyles = makeStyles(() => ({
    root: {
      flexGrow: 0,
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1100,
    },
    divider: {
      margin: "8px 16px !important",
    },
    PrimaryButton: {
      marginLeft: "16px !important",
    },
  }));

  const classes = useStyles();
  const matches = useMediaQuery(theme.breakpoints.up("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [submenus, setSubmenus] = useState(
    matches ? desktopAuthenticatedSubmenus : mobileAuthenticatedSubmenus
  );
  const [submenusUpdateCounter, setSubmenusUpdateCounter] = useState(0);

  const mobileNavLinkSx = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    py: 1.2,
    color: "hsl(178 93% 10%)",
    fontFamily: "Segoe UI Regular, sans-serif",
    fontSize: "0.95rem",
    borderRadius: "10px",
    px: 1,
    transition: "all 150ms ease",
    "&:hover": {
      backgroundColor: "hsl(153 68% 25% / 0.08)",
      color: "hsl(153 68% 25%)",
    },
  };

  const mobileNavIconSx = { fontSize: "1.125rem", opacity: 0.6 };

  const publicNavLinks = [
    {
      id: "nav.candidate.find_job",
      href: `/${i18nLocale}/job-offers`,
    },
    {
      id: "nav.candidate.companies",
      href: `/${i18nLocale}/companies`,
    },
    // {
    //   id: "nav.candidate.resume_generator",
    //   href: `/${i18nLocale}/candidates/sign_up`,
    // },
    {
      id: "menu.blog_posts",
      href: `/${i18nLocale}/candidates/posts`,
    },
  ];

  const desktopNavButtonSx = {
    textTransform: "none",
    fontFamily: "Segoe UI Regular, sans-serif",
    borderRadius: "10px",
    px: 1.75,
    py: 0.75,
    fontSize: "0.95rem",
    color: "hsl(178 93% 10%)",
    "&:hover": {
      backgroundColor: "hsl(153 68% 25% / 0.08)",
      color: "hsl(153 68% 25%)",
    },
  };

  const handleSubmenuClicked = (submenu) => {
    let tempSubmenus = submenus;
    let tempSubmenu = tempSubmenus.find((s) => s.code == submenu.code);
    tempSubmenus.forEach((s) => {
      if (s.code != submenu.code) s.isOpen = false;
    });
    tempSubmenu.isOpen = !tempSubmenu.isOpen;
    setSubmenus(tempSubmenus);
    setSubmenusUpdateCounter(submenusUpdateCounter + 1);
  };

  const handleRedirectClick = (link, popupState = null) => {
    if (link.logout) return logout(link.target);

    if (matches && popupState) {
      popupState.close();
    } else {
      setMobileOpen(false);
    }

    if (currentCandidate) {
      return navigate({
        pathname: `/${i18nLocale}/${link.target}`,
        search: link.search,
      });
    } else {
      window.location.href = `/${i18nLocale}/${link.target}`;
    }
  };

  const LogoIcon = (props) => {
    return (
      <MuiLink
        href={currentCandidate ? `/${i18nLocale}/candidate` : `/${i18nLocale}/`}
      >
        <Box
          component="img"
          id="logo-squarehub"
          alt="Squarehub"
          width="100%"
          height="auto"
          maxWidth={200}
          {...props}
          src={props.white ? LogoWhite : Logo}
        />
      </MuiLink>
    );
  };

  const logout = (target = null) => {
    sh.delete(`${i18nLocale}/candidates/sign_out`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      data: {
        authenticity_token,
      },
    })
      .then(() => {
        if (window)
          window.location.replace(
            `/${i18nLocale}/${typeof target == "string" ? target : ""}`
          );
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {});
  };

  const UserMenu = ({ classes }) => {
    const popupState = usePopupState({
      variant: "popover",
      popupId: "demoMenu",
    });

    const menuItemSx = {
      py: "0.625rem",
      px: "1rem",
      borderRadius: "0.5rem",
      mx: "0.5rem",
      gap: "0.625rem",
      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
      fontSize: "0.875rem",
      color: "hsl(178 93% 10%)",
      transition: "all 150ms ease",
      "&:hover": {
        backgroundColor: "hsl(153 68% 25% / 0.08)",
        color: "hsl(153 68% 25%)",
      },
    };

    const menuIconSx = {
      fontSize: "1.125rem",
      color: "inherit",
      opacity: 0.6,
    };

    return (
      <Box>
        <IconButton
          disableRipple={true}
          aria-label="menu-button"
          {...bindTrigger(popupState)}
          sx={{
            color: "hsl(178 93% 10%)",
            "&:hover": {
              backgroundColor: "transparent",
              opacity: 0.84,
            },
          }}
        >
          <ProfilePicture
            rounded
            picture={currentCandidate?.profile_picture_url}
            initials={
              <Typography sx={{ fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                {currentCandidate?.first_name[0]}
                {currentCandidate?.last_name ? currentCandidate?.last_name[0] : ""}
              </Typography>
            }
          />{" "}
          <ExpandMoreIcon sx={{ color: "hsl(178 93% 10%)", fontSize: "1.25rem" }} />
        </IconButton>
        <MuiMenu
          {...bindMenu(popupState)}
          disableScrollLock
          MenuListProps={{
            dense: true,
            sx: {
              py: 0.5,
              px: 0,
              width: "100%",
              minWidth: 240,
              color: "hsl(178 93% 10%)",
              "& .MuiMenuItem-root.Mui-focusVisible": {
                backgroundColor: "rgba(22, 109, 76, 0.14)",
              },
            },
          }}
          PaperProps={{
            sx: {
              minWidth: 240,
              maxWidth: 280,
              overflow: "hidden",
              "& .MuiMenuItem-root": { color: "hsl(178 93% 10%)" },
            },
          }}
        >
          <MenuItem
            sx={menuItemSx}
            onClick={() => handleRedirectClick({ target: "" }, popupState)}
          >
            <HomeOutlinedIcon sx={menuIconSx} />
            <FormattedMessage id="menu.home" />
          </MenuItem>
          <MenuItem
            sx={menuItemSx}
            onClick={() =>
              handleRedirectClick({ target: "candidate/settings" }, popupState)
            }
          >
            <SettingsOutlinedIcon sx={menuIconSx} />
            <FormattedMessage id="profile.my_settings" />
          </MenuItem>
          <MenuItem
            sx={menuItemSx}
            onClick={() =>
              handleRedirectClick(
                {
                  target: "candidate",
                  search: "?tab=jobs&onlyFavorites=true",
                },
                popupState
              )
            }
          >
            <Badge
              badgeContent={currentCandidate?.favorite_offers?.length || 0}
              max={999}
              invisible={!(currentCandidate?.favorite_offers?.length > 0)}
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.common.white,
                },
              }}
            >
              <FavoriteBorderIcon sx={menuIconSx} />
            </Badge>
            <FormattedMessage id="nav.candidate.favorite_jobs" />
          </MenuItem>
          <MenuItem
            sx={menuItemSx}
            onClick={() =>
              handleRedirectClick({ target: "candidate/messages" }, popupState)
            }
          >
            <Badge
              badgeContent={messagesUnread}
              max={999}
              invisible={!messagesUnread}
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: theme.palette.secondary.main,
                  color: theme.palette.common.white,
                },
              }}
            >
              <EmailOutlinedIcon sx={menuIconSx} />
            </Badge>
            <FormattedMessage id="menu.messages" />
          </MenuItem>
          <Divider className={classes.divider} />
          {desktopAuthenticatedSubmenus.map((submenu) => {
            return (
              <Box key={`key-${submenu.titleId}-${Math.random()}`}>
                <Typography
                  variant="body2bold"
                  sx={{
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    color: "hsl(178 93% 10%)",
                  }}
                >
                  <FormattedMessage id={submenu.titleId} />
                </Typography>
                <Box style={{ marginTop: "8px" }}>
                  {submenu.links.map((link) => {
                    return (
                      <MenuItem
                        key={`${link.titleId}-${Math.random()}`}
                        sx={menuItemSx}
                        onClick={() => {
                          handleRedirectClick(link, popupState);
                        }}
                      >
                        {link.icon && link.icon({ sx: menuIconSx })}
                        <FormattedMessage id={link.titleId} />
                      </MenuItem>
                    );
                  })}
                </Box>
                <Divider className={classes.divider} />
              </Box>
            );
          })}

          {currentCandidate && (
            <Box sx={{ mt: 1.5, mb: 1.5, px: 1 }}>
              <SelectLanguage initLocale={i18nLocale} inline compact />
            </Box>
          )}

          <Divider sx={{ mx: "1rem", my: "0.375rem", borderColor: "hsl(220 13% 91%)" }} />

          {currentCandidate && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                px: 2,
                py: 1.5,
              }}
            >
              <Button
                variant="outlined"
                onClick={logout}
                sx={{
                  textTransform: "none",
                  borderRadius: "999px",
                  borderColor: "hsl(153 68% 25%)",
                  color: "hsl(153 68% 25%)",
                  fontFamily: "Segoe UI Semibold, sans-serif",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  px: 2.75,
                  py: 0.85,
                  minWidth: "min(100%, 200px)",
                  boxSizing: "border-box",
                  "&:hover": {
                    borderColor: "hsl(153 68% 22%)",
                    color: "hsl(153 68% 22%)",
                    backgroundColor: "hsl(153 68% 25% / 0.08)",
                  },
                }}
              >
                <FormattedMessage id="logout" />
              </Button>
            </Box>
          )}
        </MuiMenu>
      </Box>
    );
  };

  const CustomUserMenu = withStyles(() => ({
    iconButton: {
      "&:hover": {
        backgroundColor: "unset",
        opacity: 0.84,
      },
    },
    userMenuItem: {
      width: "100% !important",
      maxWidth: "100%",
      boxSizing: "border-box",
      borderRadius: "0 !important",
      justifyContent: "flex-start",
      margin: 0,
      color: "hsl(178 93% 10%)",
      "&:hover": {
        backgroundColor: `${theme.palette.secondary.main} !important`,
        color: `${theme.palette.common.white} !important`,
        "& .MuiTypography-root": {
          color: `${theme.palette.common.white} !important`,
        },
        "& svg": {
          color: `${theme.palette.common.white} !important`,
        },
      },
    },
    divider: {
      margin: "8px 0 !important",
      width: "100%",
    },
  }))(UserMenu);

  const CandidateAvatar = () => {
    return <CustomUserMenu />;
  };

  return (
    <>
      <div className={classes.root}>
        <AppBar
          position="static"
          color={matches ? "transparent" : "inherit"}
          elevation={0}
          style={{
            height: matches ? "inherit" : "68px",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(8px)",
            borderBottom: "1px solid rgba(2, 51, 49, 0.08)",
          }}
        >
          <Toolbar style={{ paddingRight: matches ? "16px" : "4px" }}>
            {matches && (
              <Box flexGrow={1}>
                <LogoIcon />
              </Box>
            )}
            <Hidden mdDown>
              {currentCandidate ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      flexGrow: 1,
                      gap: 0.75,
                      mr: 1.5,
                    }}
                  >
                    <Button
                      size="small"
                      variant="text"
                      href={`/${i18nLocale}/companies`}
                      sx={desktopNavButtonSx}
                    >
                      <FormattedMessage id="menu.all_companies" />
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      href={`/${i18nLocale}/candidate/posts`}
                      sx={desktopNavButtonSx}
                    >
                      <FormattedMessage id="menu.blog_posts" />
                    </Button>
                  </Box>
                  <Button
                    size="small"
                    variant="rounded"
                    color="primaryContained"
                    href={`/${i18nLocale}/candidate?tab=jobs`}
                    sx={{
                      fontFamily: "Segoe UI Semibold, sans-serif",
                      fontWeight: 400,
                      textTransform: "none",
                      minWidth: "150px",
                      mr: "16px",
                      backgroundColor: "hsl(153 68% 25%) !important",
                      color: "white",
                      transition: "all 180ms ease",
                      "&:hover": {
                        backgroundColor: "hsl(153 68% 25% / 0.9) !important",
                      },
                    }}
                  >
                    <FormattedMessage id="menu.all_jobs" />
                  </Button>
                  <CandidateAvatar />
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexGrow: 1,
                      gap: 0.75,
                      mr: 1.5,
                    }}
                  >
                    {publicNavLinks.map((link) => (
                      <Button
                        key={link.id}
                        size="small"
                        variant="text"
                        href={link.href}
                        sx={{
                          ...desktopNavButtonSx,
                        }}
                      >
                        <FormattedMessage id={link.id} />
                      </Button>
                    ))}
                  </Box>
                  <Button
                    size="small"
                    variant="text"
                    href={`/${i18nLocale}/recruiters`}
                    sx={{
                      ...desktopNavButtonSx,
                      mr: 0.5,
                    }}
                  >
                    <FormattedMessage id="recruiter.recruiter_space" />
                  </Button>
                  <Button
                    className={classes.PrimaryButton}
                    variant="rounded"
                    size="small"
                    color="primaryContained"
                    style={{ marginRight: "16px", minWidth: "150px", textTransform: "none" }}
                    href={`/${i18nLocale}/candidates/sign_in`}
                    sx={{
                      backgroundColor: "hsl(153 68% 25%) !important",
                      color: "white",
                      fontFamily: "Segoe UI Semibold, sans-serif !important",
                      fontWeight: "400 !important",
                      transition: "all 180ms ease",
                      "&:hover": {
                        backgroundColor: "hsl(153 68% 25% / 0.9) !important",
                      },
                    }}
                  >
                    <FormattedMessage id="signin" />
                  </Button>
                  <SelectLanguage initLocale={i18nLocale} />
                </>
              )}
              <Box mx={1} />
            </Hidden>
            <Hidden lgUp>
              <IconButton
                edge="end"
                aria-label="menu"
                sx={{ color: "hsl(178 93% 10%)" }}
                onClick={() => setMobileOpen((prev) => !prev)}
              >
                {mobileOpen ? (
                  <XLucide size={24} />
                ) : (
                  <MenuLucide size={24} />
                )}
              </IconButton>
            </Hidden>
            {!matches && (
              <Box
                flexGrow={1}
                style={{ marginLeft: "-32px", textAlign: "center" }}
              >
                <LogoIcon />
              </Box>
            )}
          </Toolbar>
        </AppBar>
        <AnimatePresence>
          {mobileOpen && (
            <Box
              component={motion.div}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              sx={{
                display: { xs: "block", lg: "none" },
                backgroundColor: "white",
                borderBottom: "1px solid rgba(2, 51, 49, 0.08)",
                overflow: "hidden",
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                {currentCandidate ? (
                  <>
                    {/* Profile header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1.5,
                        pb: 1.5,
                        borderBottom: "1px solid rgba(2, 51, 49, 0.08)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor: "hsl(153 68% 25%)",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          fontSize: "0.9rem",
                          flexShrink: 0,
                        }}
                      >
                        {currentCandidate.first_name?.charAt(0).toUpperCase()}
                        {currentCandidate.last_name?.charAt(0).toUpperCase()}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "Segoe UI Semibold, sans-serif",
                            fontSize: "0.95rem",
                            color: "hsl(178 93% 10%)",
                          }}
                        >
                          {currentCandidate.first_name}{" "}
                          {currentCandidate.last_name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            color: "hsl(178 10% 45%)",
                          }}
                        >
                          {currentCandidate.email}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Nav links */}
                    <MuiLink
                      underline="none"
                      sx={mobileNavLinkSx}
                      onClick={() => {
                        handleRedirectClick({ target: "" });
                      }}
                    >
                      <HomeOutlinedIcon sx={mobileNavIconSx} />
                      <FormattedMessage id="menu.home" />
                    </MuiLink>
                    <MuiLink
                      underline="none"
                      sx={mobileNavLinkSx}
                      onClick={() => {
                        setMobileOpen(false);
                        if (window)
                          window.location.href = `/${i18nLocale}/candidate?tab=jobs`;
                      }}
                    >
                      <Search sx={mobileNavIconSx} />
                      <FormattedMessage id="nav.candidate.find_job" />
                    </MuiLink>
                    <MuiLink
                      underline="none"
                      sx={mobileNavLinkSx}
                      onClick={() => {
                        handleRedirectClick({ target: "candidate/companies" });
                      }}
                    >
                      <BusinessIcon sx={mobileNavIconSx} />
                      <FormattedMessage id="nav.candidate.companies" />
                    </MuiLink>
                    <MuiLink
                      underline="none"
                      sx={mobileNavLinkSx}
                      onClick={() => {
                        setMobileOpen(false);
                        if (window)
                          window.location.href = `/${i18nLocale}/candidate?tab=cv`;
                      }}
                    >
                      <CreateNewFolderIcon sx={mobileNavIconSx} />
                      <Box sx={{ position: "relative", display: "inline-flex" }}>
                        <FormattedMessage id="nav.candidate.resume_generator" />
                        <Box
                          sx={{
                            position: "absolute",
                            top: -6,
                            right: -30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 0.8,
                            height: "16px",
                            whiteSpace: "nowrap",
                            borderRadius: "10px",
                            color: "white",
                            backgroundColor: "hsl(153 68% 25%)",
                            fontSize: "0.6rem",
                            fontFamily: "Segoe UI Semibold, sans-serif",
                          }}
                        >
                          <FormattedMessage id="generate_cv.new" />
                        </Box>
                      </Box>
                    </MuiLink>
                    <MuiLink
                      underline="none"
                      sx={mobileNavLinkSx}
                      onClick={() => {
                        setMobileOpen(false);
                        if (window)
                          window.location.href = `/${i18nLocale}/candidate?tab=applications`;
                      }}
                    >
                      <WorkOutlinedIcon sx={mobileNavIconSx} />
                      <FormattedMessage id="nav.candidate.my_jobs" />
                      {currentCandidate.unclosed_job_applications_count &&
                        parseInt(currentCandidate.unclosed_job_applications_count) > 0 ? (
                          <Box
                            sx={{
                              ml: "auto",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: "20px",
                              height: "20px",
                              px: "6px",
                              borderRadius: "10px",
                              color: "white",
                              backgroundColor: "hsl(153 68% 25%)",
                              fontSize: "0.7rem",
                              fontFamily: "Segoe UI Semibold, sans-serif",
                              lineHeight: 1,
                            }}
                          >
                            {currentCandidate.unclosed_job_applications_count}
                          </Box>
                        ) : null}
                    </MuiLink>
                    <MuiLink
                      underline="none"
                      sx={mobileNavLinkSx}
                      onClick={() => {
                        setMobileOpen(false);
                        if (window)
                          window.location.href = `/${i18nLocale}/candidate/posts`;
                      }}
                    >
                      <FeedIcon sx={mobileNavIconSx} />
                      <FormattedMessage id="menu.blog_posts" />
                    </MuiLink>

                    <Divider sx={{ my: 1 }} />

                    {/* Collapsible submenus */}
                    {submenus.map((submenu) => (
                      <Box key={`key-${submenu.titleId}`}>
                        <MuiLink
                          underline="none"
                          sx={{
                            ...mobileNavLinkSx,
                            justifyContent: "space-between",
                            cursor: "pointer",
                          }}
                          onClick={() => handleSubmenuClicked(submenu)}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Segoe UI Semibold, sans-serif",
                              fontSize: "0.95rem",
                            }}
                          >
                            <FormattedMessage id={submenu.titleId} />
                          </Typography>
                          {submenu.isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </MuiLink>
                        <Collapse in={submenu.isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ pl: 2 }}>
                            {submenu.links.map((link) => (
                              <MuiLink
                                key={link.titleId}
                                underline="none"
                                sx={{
                                  ...mobileNavLinkSx,
                                  fontSize: "0.875rem",
                                  py: 0.8,
                                }}
                                onClick={() => handleRedirectClick(link)}
                              >
                                {link.icon && link.icon()}
                                <FormattedMessage id={link.titleId} />
                              </MuiLink>
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    ))}

                    <Divider sx={{ my: 1 }} />

                    <MuiLink
                      underline="none"
                      sx={mobileNavLinkSx}
                      onClick={() => {
                        handleRedirectClick({ target: "candidate/messages" });
                      }}
                    >
                      <Badge
                        badgeContent={messagesUnread}
                        max={999}
                        invisible={!messagesUnread}
                        sx={{
                          "& .MuiBadge-badge": {
                            backgroundColor: theme.palette.secondary.main,
                            color: theme.palette.common.white,
                          },
                        }}
                      >
                        <EmailOutlinedIcon sx={mobileNavIconSx} />
                      </Badge>
                      <FormattedMessage id="menu.messages" />
                    </MuiLink>

                    <MuiLink
                      underline="none"
                      sx={mobileNavLinkSx}
                      onClick={() => {
                        handleRedirectClick({ target: "candidate/settings" });
                      }}
                    >
                      <SettingsOutlinedIcon sx={mobileNavIconSx} />
                      <FormattedMessage id="nav.my_settings" />
                    </MuiLink>

                    {/* Language selector */}
                    <Box sx={{ mt: 1.5 }}>
                      <SelectLanguage initLocale={i18nLocale} inline compact />
                    </Box>

                    {/* Logout */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 1.5,
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={logout}
                        fullWidth
                        sx={{
                          maxWidth: 280,
                          textTransform: "none",
                          borderRadius: "999px",
                          borderColor: "hsl(153 68% 25%)",
                          color: "hsl(153 68% 25%)",
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          py: 0.85,
                          "&:hover": {
                            borderColor: "hsl(153 68% 22%)",
                            color: "hsl(153 68% 22%)",
                            backgroundColor: "hsl(153 68% 25% / 0.08)",
                          },
                        }}
                      >
                        <FormattedMessage id="logout" />
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    {/* Unauthenticated mobile menu */}
                    {publicNavLinks.map((link) => (
                      <MuiLink
                        key={link.id}
                        href={link.href}
                        underline="none"
                        sx={mobileNavLinkSx}
                        onClick={() => setMobileOpen(false)}
                      >
                        <FormattedMessage id={link.id} />
                      </MuiLink>
                    ))}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        href={`/${i18nLocale}/recruiters`}
                        sx={{
                          textTransform: "none",
                          borderRadius: "12px",
                          py: 1.2,
                          fontSize: "0.95rem",
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          color: "hsl(178 93% 10%)",
                          backgroundColor: "white",
                          border: "1px solid rgba(2, 51, 49, 0.2)",
                          "&:hover": {
                            backgroundColor: "hsl(0 0% 97%)",
                            borderColor: "rgba(2, 51, 49, 0.35)",
                          },
                        }}
                      >
                        <FormattedMessage id="recruiter.recruiter_space" />
                      </Button>
                      <Button
                        variant="contained"
                        fullWidth
                        href={`/${i18nLocale}/candidates/sign_in`}
                        sx={{
                          textTransform: "none",
                          borderRadius: "12px",
                          py: 1.2,
                          fontSize: "0.95rem",
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          backgroundColor: "hsl(153 68% 25%)",
                          color: "white",
                          transition: "all 180ms ease",
                          "&:hover": {
                            backgroundColor: "hsl(153 68% 22%)",
                          },
                        }}
                      >
                        <FormattedMessage id="signin" />
                      </Button>
                    </Box>
                    <Box sx={{ mt: 1.5 }}>
                      <SelectLanguage initLocale={i18nLocale} />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </AnimatePresence>
      </div>
      <div style={{ height: matches ? "64px" : "68px" }} />
      {children}
    </>
  );
}
